import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFullKBReviewStore } from '../stores/fullKBReviewStore';

const FullKBReviewProgress: React.FC = () => {
  const { subProjectId, reviewId } = useParams<{ subProjectId: string; reviewId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { progress, transcriptId, startPolling, stopPolling, cancelReview } =
    useFullKBReviewStore();

  useEffect(() => {
    if (!subProjectId || !reviewId) return;
    startPolling(subProjectId, reviewId);
    return () => stopPolling();
  }, [subProjectId, reviewId]);

  useEffect(() => {
    if (progress?.status === 'completed' && transcriptId) {
      window.open(`/search-transcript/${transcriptId}`, '_blank');
    }
  }, [progress?.status, transcriptId]);

  const handleCancel = async () => {
    if (subProjectId) await cancelReview(subProjectId);
  };

  const pct = (done: number, total: number) =>
    total > 0 ? Math.round((done / total) * 100) : 0;

  const isFinished = ['completed', 'error', 'cancelled'].includes(progress?.status ?? '');

  return (
    <div style={{ maxWidth: 720, margin: '2rem auto', padding: '0 1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.2rem', fontWeight: 600, margin: 0 }}>
          {t('fullKbReview.progressTitle', 'Full KB Review')}
        </h1>
        {!isFinished && (
          <button
            onClick={handleCancel}
            style={{
              marginLeft: 'auto',
              padding: '4px 12px',
              fontSize: '0.8rem',
              background: '#fee2e2',
              color: '#991b1b',
              border: '1px solid #fca5a5',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            {t('fullKbReview.cancelBtn', 'Cancel Review')}
          </button>
        )}
      </div>

      {!progress ? (
        <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>{t('common.loading', 'Loading...')}</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {/* Status / Batch label */}
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '0.75rem 1rem' }}>
            <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: 500 }}>
              {progress.batch_label || progress.status}
            </p>
            {progress.current_document && (
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: '#6b7280' }}>
                {t('fullKbReview.processing', 'Processing:')} {progress.current_document}
              </p>
            )}
          </div>

          {/* Pass 1 bar */}
          {progress.pass1_total > 0 && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#6b7280', marginBottom: 4 }}>
                <span>{t('fullKbReview.pass1Label', 'Pass 1 — Relevance screening')}</span>
                <span>{progress.pass1_processed}/{progress.pass1_total} ({progress.pass1_relevant} {t('fullKbReview.relevant', 'relevant')})</span>
              </div>
              <div style={{ background: '#e2e8f0', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                <div
                  style={{
                    background: '#3b82f6',
                    height: '100%',
                    width: `${pct(progress.pass1_processed, progress.pass1_total)}%`,
                    transition: 'width 0.5s ease',
                  }}
                />
              </div>
            </div>
          )}

          {/* Pass 2 bar */}
          {progress.pass2_total > 0 && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#6b7280', marginBottom: 4 }}>
                <span>{t('fullKbReview.pass2Label', 'Pass 2 — State-of-the-art enrichment')}</span>
                <span>{progress.pass2_processed}/{progress.pass2_total}</span>
              </div>
              <div style={{ background: '#e2e8f0', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                <div
                  style={{
                    background: '#10b981',
                    height: '100%',
                    width: `${pct(progress.pass2_processed, progress.pass2_total)}%`,
                    transition: 'width 0.5s ease',
                  }}
                />
              </div>
            </div>
          )}

          {/* Token counter */}
          {progress.total_tokens_used > 0 && (
            <p style={{ fontSize: '0.78rem', color: '#9ca3af', margin: 0 }}>
              {t('fullKbReview.tokensUsed', 'Tokens used:')} {progress.total_tokens_used.toLocaleString()}
            </p>
          )}

          {/* Error */}
          {progress.error_message && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: '0.75rem 1rem' }}>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#991b1b' }}>{progress.error_message}</p>
            </div>
          )}

          {/* Completed / cancelled */}
          {progress.status === 'completed' && (
            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, padding: '0.75rem 1rem' }}>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#166534' }}>
                {t('fullKbReview.completed', 'Review completed. The transcript has opened in a new tab.')}
              </p>
            </div>
          )}

          {progress.status === 'cancelled' && (
            <div style={{ background: '#fefce8', border: '1px solid #fde047', borderRadius: 6, padding: '0.75rem 1rem' }}>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#713f12' }}>
                {t('fullKbReview.cancelled', 'Review was cancelled. Partial state-of-the-art has been saved.')}
              </p>
            </div>
          )}

          {/* Back link */}
          {isFinished && (
            <button
              onClick={() => navigate(-1)}
              style={{
                alignSelf: 'flex-start',
                padding: '6px 14px',
                fontSize: '0.85rem',
                background: '#f1f5f9',
                border: '1px solid #cbd5e1',
                borderRadius: 4,
                cursor: 'pointer',
              }}
            >
              {t('common.back', 'Back')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default FullKBReviewProgress;
